import { create } from 'zustand';
import { api } from './api';
import { desktop } from './desktop';

/**
 * "Report a bug": what the dialog sends besides what the person types. The
 * page keeps its last errors and warnings in memory from the moment it loads
 * (nothing leaves the browser until someone sends a report), and
 * bugContext() describes the browser, the machine and the page.
 */

interface LogLine {
  at: number;
  level: 'error' | 'warn';
  text: string;
}

const MAX_LINES = 200;
const lines: LogLine[] = [];
const startedAt = Date.now();

function describe(v: unknown): string {
  if (v instanceof Error) return v.stack && v.stack.includes(v.message) ? v.stack : `${v.name}: ${v.message}${v.stack ? `\n${v.stack}` : ''}`;
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function record(level: LogLine['level'], args: unknown[]) {
  const text = args.map(describe).join(' ').slice(0, 4000);
  lines.push({ at: Date.now(), level, text });
  if (lines.length > MAX_LINES) lines.splice(0, lines.length - MAX_LINES);
}

let installed = false;

/** Starts keeping the page's errors and warnings. Call once, as early as possible. */
export function installErrorLog() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  for (const level of ['error', 'warn'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      record(level, args);
      original(...args);
    };
  }
  window.addEventListener('error', (e) => record('error', [e.error ?? `${e.message} (${e.filename}:${e.lineno}:${e.colno})`]));
  window.addEventListener('unhandledrejection', (e) => record('error', ['Unhandled rejection:', e.reason]));
}

const stamp = (at: number) => `+${((at - startedAt) / 1000).toFixed(1)}s`;

/** The last few errors, for the report's description. */
export function recentErrors(max = 10): string {
  return lines
    .filter((l) => l.level === 'error')
    .slice(-max)
    .map((l) => `[${stamp(l.at)}] ${l.text}`)
    .join('\n');
}

/** Everything kept, as a file to attach (null when nothing went wrong). */
export function errorLogFile(): File | null {
  if (!lines.length) return null;
  const text = lines.map((l) => `[${stamp(l.at)}] ${l.level.toUpperCase()} ${l.text}`).join('\n');
  return new File([`Page opened ${new Date(startedAt).toISOString()}\n\n${text}\n`], 'console.log', { type: 'text/plain' });
}

function gpu(): string {
  try {
    const gl = document.createElement('canvas').getContext('webgl2') ?? document.createElement('canvas').getContext('webgl');
    if (!gl) return 'no WebGL';
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const name = info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return `${String(name)}${gl instanceof WebGL2RenderingContext ? '' : ' (WebGL 1)'}`;
  } catch {
    return 'unknown';
  }
}

function browser(ua: string): string {
  const m =
    /(Edg|OPR|Firefox|Chrome|Version)\/([\d.]+)/.exec(ua.includes('Edg/') ? ua.slice(ua.indexOf('Edg/')) : ua.includes('OPR/') ? ua.slice(ua.indexOf('OPR/')) : ua);
  if (!m) return ua.slice(0, 120);
  const name = { Edg: 'Edge', OPR: 'Opera', Firefox: 'Firefox', Chrome: 'Chrome', Version: 'Safari' }[m[1]] ?? m[1];
  return `${name} ${m[2]}`;
}

function os(ua: string): string {
  const m = /(Windows NT [\d.]+|Mac OS X [\d_]+|Android [\d.]+|iPhone OS [\d_]+|iPad; CPU OS [\d_]+|CrOS|Linux)/.exec(ua);
  return m ? m[1].replace(/_/g, '.') : navigator.platform || 'unknown';
}

/** What a report says about where it came from. */
export function bugContext(): Record<string, string | number> {
  const ua = navigator.userAgent;
  const nav = navigator as Navigator & { deviceMemory?: number; connection?: { effectiveType?: string } };
  const ctx: Record<string, string | number> = {
    page: location.pathname + location.search,
    app: desktop ? `desktop ${desktop.version} (${desktop.platform})` : 'web',
    browser: browser(ua),
    os: os(ua),
    gpu: gpu(),
    cpuCores: navigator.hardwareConcurrency || 'unknown',
    window: `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}x`,
    screen: `${screen.width}x${screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    pageOpenFor: `${Math.round((Date.now() - startedAt) / 60_000)} min`,
    userAgent: ua,
  };
  if (nav.deviceMemory) ctx.memory = `${nav.deviceMemory} GB`;
  const heap = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
  if (heap) ctx.jsHeap = `${Math.round(heap.usedJSHeapSize / 1024 / 1024)} MB`;
  if (nav.connection?.effectiveType) ctx.network = nav.connection.effectiveType;
  if (!navigator.onLine) ctx.network = 'offline';
  return ctx;
}

/** Whether this server files bug reports (asked once per page load). */
let enabled: Promise<boolean> | null = null;
export function bugReportsEnabled(): Promise<boolean> {
  enabled ??= api<{ bugReports?: boolean }>('/api/config')
    .then((c) => !!c.bugReports)
    .catch(() => {
      enabled = null;
      return false;
    });
  return enabled;
}

/** The dialog's open state; any page or menu can open it. */
export const bugReportUi = create<{ open: boolean }>(() => ({ open: false }));
export const openBugReport = () => bugReportUi.setState({ open: true });
